import { createFileRoute } from '@tanstack/react-router';
import { type CSSProperties, type FormEvent, useMemo, useState, Fragment } from 'react';

import type { CreateAccrualTemplate } from '@dcc-erp/shared';
import { fmt, fmtB } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { BranchSelect, DepartmentSelect } from '../components/dimensions';
import { PageHead } from '../components/layout';
import { SearchSelect } from '../components/SearchSelect';
import {
  useAccrualTemplates,
  useCreateAccrualTemplate,
  useReverseAccruals,
  useRunAccruals,
} from '../lib/queries/accruals';
import { useCoa } from '../lib/queries/coa';
import { useActiveCompanyId } from '../lib/queries/companies';

// The 'พ.ค. 2026' demo period the prototype is anchored to — keeps the UI
// match exact while we surface real run/reverse mutations against the API.
// Phase 3d will swap this to a real period picker.
const DEMO_PERIOD = '202605';
const PRIOR_PERIOD = '202604';

type TabKey = 'matrix' | 'outstanding' | 'booking' | 'settings';
type MethodKey = '3m_avg' | 'last_m' | 'last_yr' | '6m_avg' | 'manual';

interface ExpType {
  code: string;
  label: string;
  acct: string;
  tone: string;
  method: MethodKey;
  variance: number;
  description: string;
}

interface Branch {
  id: string;
  name: string;
  region: string;
}

interface Cell {
  m1: number;
  m2: number;
  m3: number;
  estimate: number;
  billArrived: boolean;
  actual: number | null;
  baseAcct: string;
}

interface OutstandingItem extends Cell {
  id: string;
  branch: Branch;
  exp: ExpType;
  age: number;
  daysOld: number;
}

interface Totals {
  estimated: number;
  billed: number;
  outstanding: number;
  cellCount: number;
  billedCount: number;
  accrued: number;
}

const EXP_TYPES: readonly ExpType[] = [
  {
    code: 'ELEC',
    label: 'ค่าไฟฟ้า',
    acct: '5131',
    tone: '#b54708',
    method: '3m_avg',
    variance: 0.18,
    description: 'ผันแปรตามฤดูกาล',
  },
  {
    code: 'WATER',
    label: 'ค่าน้ำประปา',
    acct: '5132',
    tone: '#1a56a6',
    method: '3m_avg',
    variance: 0.1,
    description: 'ผันแปรเล็กน้อย',
  },
  {
    code: 'NET',
    label: 'ค่า Internet',
    acct: '6216',
    tone: '#117a3d',
    method: 'last_m',
    variance: 0.02,
    description: 'ค่าคงที่ตามแพ็คเกจ',
  },
  {
    code: 'TEL',
    label: 'ค่าโทรศัพท์',
    acct: '6216',
    tone: '#0e7490',
    method: 'last_m',
    variance: 0.05,
    description: 'ค่าคงที่ส่วนใหญ่',
  },
  {
    code: 'SECURITY',
    label: 'ค่าบริการ รปภ.',
    acct: '6217',
    tone: '#7030A0',
    method: 'last_m',
    variance: 0.01,
    description: 'สัญญาคงที่',
  },
  {
    code: 'CLEAN',
    label: 'ค่าทำความสะอาด',
    acct: '6217',
    tone: '#a16207',
    method: 'last_m',
    variance: 0.02,
    description: 'สัญญาคงที่',
  },
];

const METHODS: Readonly<Record<MethodKey, { label: string; icon: string }>> = {
  '3m_avg': { label: 'ค่าเฉลี่ย 3 เดือน', icon: '~' },
  last_m: { label: 'ใช้เดือนล่าสุด', icon: '↑' },
  last_yr: { label: 'เดือนเดียวกันปีก่อน', icon: '◷' },
  '6m_avg': { label: 'ค่าเฉลี่ย 6 เดือน', icon: '≈' },
  manual: { label: 'ระบุเอง', icon: '✎' },
};

// Real branches and accrual cells come from /accruals/entries (Phase 4). For
// now, the matrix tab is shown as an empty placeholder; the mock generator was
// removed. ACC_BRANCHES is kept empty so the remaining DOM references that
// haven't been refactored yet (RunModal preview) render harmlessly.
const ACC_BRANCHES: readonly Branch[] = [];

function getCell(_branch: Branch, exp: ExpType): Cell {
  return {
    m1: 0,
    m2: 0,
    m3: 0,
    estimate: 0,
    billArrived: false,
    actual: null,
    baseAcct: exp.acct,
  };
}

function AccrualEmpty({ title, message }: { title: string; message: string }) {
  return (
    <div className="card">
      <div className="card-body" style={{ padding: 40, textAlign: 'center' }}>
        <div className="muted" style={{ fontSize: 15 }}>
          {title}
        </div>
        <div className="meta" style={{ marginTop: 6 }}>
          {message}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/accrual')({
  component: AccrualPage,
});

function AccrualPage() {
  const [tab, setTab] = useState<TabKey>('matrix');
  const [showBookActual, setShowBookActual] = useState<OutstandingItem | null>(null);
  const [showRunResult, setShowRunResult] = useState<boolean>(false);
  const [showAddTemplate, setShowAddTemplate] = useState<boolean>(false);

  // API hooks — real run/reverse mutations. The Matrix data stays mock-driven
  // (per the prototype) until master-data templates land in the DB; the run
  // and reverse buttons hit the real endpoints so the GL is correct.
  const companyId = useActiveCompanyId();
  const runMutation = useRunAccruals(companyId);
  const reverseMutation = useReverseAccruals(companyId);
  const [methods, setMethods] = useState<Record<string, MethodKey>>(() => {
    const obj: Record<string, MethodKey> = {};
    EXP_TYPES.forEach((t) => {
      obj[t.code] = t.method;
    });
    return obj;
  });

  // Real accrual entries / templates aren't aggregated yet — surface 0s in the
  // KPI cards rather than the prototype's 200×6 mock matrix. Phase 4 will wire
  // /companies/:cid/accruals/entries into both the matrix and KPIs.
  const totals: Totals = {
    estimated: 0,
    billed: 0,
    outstanding: 0,
    cellCount: 0,
    billedCount: 0,
    accrued: 0,
  };
  const outstandingList: OutstandingItem[] = [];

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['matrix', 'Matrix ประมาณการ (สาขา × ประเภท)'],
    ['outstanding', 'รายการรอบิลจริง'],
    ['booking', 'บันทึกค่าใช้จ่ายจริง'],
    ['settings', 'ตั้งค่าวิธีประมาณการ'],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['ลูกหนี้ / เจ้าหนี้', 'ประมาณการค่าใช้จ่ายประจำ']}
        title="ประมาณการค่าใช้จ่ายประจำของสาขา — งวด พ.ค. 2026"
        actions={
          <>
            <select className="select" defaultValue="may26">
              <option value="may26">งวด: พ.ค. 2026</option>
              <option value="apr26">งวด: เม.ย. 2026</option>
            </select>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              <Icon name="download" size={14} /> ส่งออก Matrix
            </button>
            <button
              className="btn"
              disabled={!companyId || reverseMutation.isPending}
              onClick={() => {
                if (!companyId) return;
                reverseMutation.mutate({ period: PRIOR_PERIOD });
              }}
              title={`กลับประมาณการงวด ${PRIOR_PERIOD}`}
            >
              <Icon name="refresh" size={14} />{' '}
              {reverseMutation.isPending ? 'กำลังกลับ...' : 'กลับประมาณการเดือนก่อน'}
            </button>
            <button className="btn" onClick={() => setShowAddTemplate(true)} disabled={!companyId}>
              <Icon name="plus" size={14} /> เพิ่ม Template
            </button>
            <button className="btn primary" onClick={() => setShowRunResult(true)}>
              <Icon name="check" size={14} /> รันประมาณการทุกสาขา
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ประมาณการรวม (Accrual)
          </div>
          <div className="value tnum">
            {fmtB(totals.estimated)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            {totals.cellCount} รายการ ({ACC_BRANCHES.length} สาขา × {EXP_TYPES.length} ประเภท)
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            ค่าใช้จ่ายค้างจ่ายคงเหลือ
          </div>
          <div className="value tnum">
            {fmtB(totals.outstanding)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">บัญชี 2150 — {totals.accrued} รายการรอบิลจริง</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            บิลจริงมาแล้ว · กลับประมาณการ
          </div>
          <div className="value tnum">
            {fmt(totals.billedCount)} <span className="unit">/ {totals.cellCount} รายการ</span>
          </div>
          <div className="trend pos">
            <Icon name="check" size={11} /> ปรับเป็นยอดจริง {fmtB(totals.billed)} บาท
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            ค้างนาน &gt; 2 เดือน
          </div>
          <div className="value tnum">
            {fmt(outstandingList.filter((o) => o.daysOld > 60).length)}{' '}
            <span className="unit">รายการ</span>
          </div>
          <div className="trend down">ควรติดตามบิลจริง</div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          วงจรการบันทึก — 3 ขั้นตอน
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          <div
            style={{
              display: 'flex',
              gap: 10,
              padding: '10px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#1a56a6',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontSize: 14,
                fontFamily: 'var(--font-display)',
                flexShrink: 0,
              }}
            >
              1
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>ก่อนปิดงวด — ตั้งประมาณการ</div>
              <div className="code muted" style={{ fontSize: 13, marginTop: 3 }}>
                Dr. ค่าใช้จ่าย / Cr. ค่าใช้จ่ายค้างจ่าย (2150)
              </div>
              <div className="meta" style={{ marginTop: 3 }}>
                ใช้ค่าเฉลี่ย 3 เดือน (เปลี่ยนได้ต่อหมวด)
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 10,
              padding: '10px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#b42318',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontSize: 14,
                fontFamily: 'var(--font-display)',
                flexShrink: 0,
              }}
            >
              2
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>เมื่อบิลจริงมา — กลับประมาณการ</div>
              <div className="code muted" style={{ fontSize: 13, marginTop: 3 }}>
                Dr. ค่าใช้จ่ายค้างจ่าย (2150) / Cr. ค่าใช้จ่าย
              </div>
              <div className="meta" style={{ marginTop: 3 }}>
                ระบบเตือนอัตโนมัติเมื่อมีคู่ที่ accrued
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 10,
              padding: '10px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#117a3d',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontSize: 14,
                fontFamily: 'var(--font-display)',
                flexShrink: 0,
              }}
            >
              3
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>บันทึกค่าใช้จ่ายจริง</div>
              <div className="code muted" style={{ fontSize: 13, marginTop: 3 }}>
                Dr. ค่าใช้จ่าย / Cr. เจ้าหนี้ (2110)
              </div>
              <div className="meta" style={{ marginTop: 3 }}>
                ตามใบกำกับภาษีจริง
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'matrix' && (
        <AccrualEmpty title="ยังไม่มีข้อมูลสำหรับช่วงเวลานี้" message="เพิ่ม template เพื่อเริ่ม" />
      )}
      {tab === 'outstanding' && (
        <AccrualEmpty
          title="ยังไม่มีข้อมูลสำหรับช่วงเวลานี้"
          message="ตั้ง template + รันประมาณการเพื่อสร้างรายการ"
        />
      )}
      {tab === 'booking' && (
        <BookingWorkflow onBook={(o) => setShowBookActual(o)} outstandingList={outstandingList} />
      )}
      {tab === 'settings' && <MethodSettings methods={methods} setMethods={setMethods} />}

      {showBookActual && (
        <BookActualModal item={showBookActual} onClose={() => setShowBookActual(null)} />
      )}
      {showRunResult && (
        <RunModal
          totals={totals}
          branches={ACC_BRANCHES}
          methods={methods}
          onClose={() => setShowRunResult(false)}
          isRunning={runMutation.isPending}
          onConfirm={() => {
            if (!companyId) return;
            runMutation.mutate(
              { period: DEMO_PERIOD },
              { onSuccess: () => setShowRunResult(false) },
            );
          }}
        />
      )}
      {showAddTemplate && companyId && (
        <AccrualTemplateModal companyId={companyId} onClose={() => setShowAddTemplate(false)} />
      )}
    </div>
  );
}

// ============================================================
// AccrualTemplateModal (Phase 42) — create a recurring-accrual template with
// its branch (สาขา) + cost-center (หน่วยงาน) dimensions, which then propagate
// to every monthly accrual JV the template generates.
// ============================================================

interface AccrualTemplateModalProps {
  companyId: string;
  onClose: () => void;
}

function AccrualTemplateModal({ companyId, onClose }: AccrualTemplateModalProps) {
  const createTemplate = useCreateAccrualTemplate(companyId);
  const { data: templates = [] } = useAccrualTemplates(companyId);
  const { data: coa = [] } = useCoa(companyId, true);
  // Postable expense/cost accounts only (control/header accounts can't be posted).
  // Memoized on `coa` so the searchable picker's options/label-map keep a stable
  // identity between renders.
  const expenseAccountOptions = useMemo(
    () =>
      coa
        .filter((a) => (a.type === 'EXPENSE' || a.type === 'COST') && !a.isControl)
        .map((a) => ({ value: a.code, label: `${a.code} — ${a.nameTh}` })),
    [coa],
  );
  const expenseLabelByCode = useMemo(
    () => new Map(expenseAccountOptions.map((o) => [o.value, o.label] as const)),
    [expenseAccountOptions],
  );

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [expenseAccountCode, setExpenseAccountCode] = useState('');
  const [accruedLiabilityAccountCode, setAccruedLiabilityAccountCode] = useState('2160');
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [branchId, setBranchId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const amt = Number(monthlyAmount.replace(/,/g, ''));
  const canSubmit =
    code.trim() &&
    name.trim() &&
    expenseAccountCode &&
    Number.isFinite(amt) &&
    amt > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(startDate) &&
    !createTemplate.isPending;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    const data: CreateAccrualTemplate = {
      code: code.trim(),
      name: name.trim(),
      expenseAccountCode,
      accruedLiabilityAccountCode: accruedLiabilityAccountCode.trim() || '2160',
      monthlyAmount: amt,
      branchId: branchId || null,
      departmentId: departmentId || null,
      isActive: true,
      startDate,
      ...(endDate ? { endDate } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    try {
      await createTemplate.mutateAsync(data);
      onClose();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  const overlay: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,15,20,0.45)',
    display: 'grid',
    placeItems: 'center',
    zIndex: 100,
    padding: 24,
  };
  const label: CSSProperties = {
    fontSize: 13,
    color: 'var(--text-muted)',
    marginBottom: 6,
    display: 'block',
  };

  return (
    <div style={overlay} onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void handleSubmit(e)}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 640,
          maxWidth: '100%',
          maxHeight: '94vh',
          overflow: 'auto',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>เพิ่ม Template ประมาณการ</h3>
            <div className="meta" style={{ marginTop: 2 }}>
              ค่าใช้จ่ายประจำที่ตั้งประมาณการทุกเดือน — สาขา/หน่วยงานจะติดไปกับ JV ทุกงวด
            </div>
          </div>
          <button
            type="button"
            className="btn ghost"
            onClick={onClose}
            style={{ padding: '0 8px' }}
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <div>
              <label style={label}>รหัส *</label>
              <input
                className="input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="ACR-ELEC"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={label}>ชื่อรายการ *</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ค่าไฟฟ้า — โรงงาน 2"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={label}>บัญชีค่าใช้จ่าย *</label>
              <SearchSelect
                value={expenseAccountCode}
                onChange={setExpenseAccountCode}
                options={expenseAccountOptions}
                getLabel={(v) => expenseLabelByCode.get(v) ?? v}
                placeholder="— เลือกบัญชี —"
                ariaLabel="บัญชีค่าใช้จ่าย"
              />
            </div>
            <div>
              <label style={label}>บัญชีค่าใช้จ่ายค้างจ่าย</label>
              <input
                className="input"
                value={accruedLiabilityAccountCode}
                onChange={(e) => setAccruedLiabilityAccountCode(e.target.value)}
                placeholder="2160"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={label}>สาขา</label>
              <BranchSelect companyId={companyId} value={branchId} onChange={setBranchId} />
            </div>
            <div>
              <label style={label}>หน่วยงาน</label>
              <DepartmentSelect
                companyId={companyId}
                value={departmentId}
                onChange={setDepartmentId}
                branchId={branchId}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={label}>ประมาณการ/เดือน (บาท) *</label>
              <input
                type="number"
                className="input tnum"
                value={monthlyAmount}
                onChange={(e) => setMonthlyAmount(e.target.value)}
                min={0}
                step="0.01"
                style={{ width: '100%', textAlign: 'right' }}
              />
            </div>
            <div>
              <label style={label}>เริ่ม *</label>
              <input
                type="date"
                className="input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={label}>สิ้นสุด (ถ้ามี)</label>
              <input
                type="date"
                className="input"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div>
            <label style={label}>หมายเหตุ</label>
            <input
              className="input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          {err && (
            <div
              style={{
                background: 'rgba(180,35,24,0.08)',
                color: '#b42318',
                padding: '8px 12px',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {err}
            </div>
          )}

          <div className="meta" style={{ fontSize: 12 }}>
            มี {templates.length} template อยู่แล้ว
          </div>
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button type="submit" className="btn primary" disabled={!canSubmit}>
            <Icon name="check" size={14} />{' '}
            {createTemplate.isPending ? 'กำลังบันทึก...' : 'บันทึก Template'}
          </button>
        </div>
      </form>
    </div>
  );
}

interface MatrixProps {
  branches: readonly Branch[];
  region: string;
  setRegion: (s: string) => void;
  expF: string;
  setExpF: (s: string) => void;
  q: string;
  setQ: (s: string) => void;
  methods: Record<string, MethodKey>;
}

function _AccrualMatrix({
  branches,
  region,
  setRegion,
  expF,
  setExpF,
  q,
  setQ,
  methods,
}: MatrixProps) {
  const types = expF === 'all' ? EXP_TYPES : EXP_TYPES.filter((t) => t.code === expF);

  return (
    <>
      <div className="tbl-shell">
        <div className="filter-row">
          <input
            className="input search w-lg"
            placeholder="ค้นหา รหัสสาขา / ชื่อสาขา"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              paddingLeft: 32,
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")",
              backgroundRepeat: 'no-repeat',
              backgroundPosition: '10px center',
            }}
          />
          <select className="select" value={region} onChange={(e) => setRegion(e.target.value)}>
            <option value="all">ภูมิภาค: ทั้งหมด</option>
            <option value="กทม.">กทม. และปริมณฑล</option>
            <option value="กลาง">ภาคกลาง</option>
            <option value="เหนือ">ภาคเหนือ</option>
            <option value="อีสาน">ภาคอีสาน</option>
            <option value="ตะวันออก">ภาคตะวันออก</option>
            <option value="ตะวันตก">ภาคตะวันตก</option>
            <option value="ใต้">ภาคใต้</option>
          </select>
          <select className="select" value={expF} onChange={(e) => setExpF(e.target.value)}>
            <option value="all">ประเภทค่าใช้จ่าย: ทุกประเภท</option>
            {EXP_TYPES.map((t) => (
              <option key={t.code} value={t.code}>
                {t.label}
              </option>
            ))}
          </select>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="meta">
              {branches.length} สาขา × {types.length} ประเภท = {branches.length * types.length}{' '}
              รายการ
            </span>
          </div>
        </div>

        <div className="tbl-wrap" style={{ maxHeight: 620, overflowX: 'auto' }}>
          <table className="tbl" style={{ minWidth: 1600 }}>
            <thead>
              <tr>
                <th
                  rowSpan={2}
                  style={{
                    width: 90,
                    position: 'sticky',
                    left: 0,
                    background: '#fafafa',
                    zIndex: 2,
                  }}
                >
                  สาขา
                </th>
                <th
                  rowSpan={2}
                  style={{
                    width: 180,
                    position: 'sticky',
                    left: 90,
                    background: '#fafafa',
                    zIndex: 2,
                    borderRight: '2px solid var(--border-strong)',
                  }}
                >
                  ชื่อสาขา
                </th>
                {types.map((t) => {
                  const mk = methods[t.code] ?? t.method;
                  return (
                    <th
                      key={t.code}
                      colSpan={5}
                      className="num"
                      style={{
                        textAlign: 'center',
                        background: t.tone + '15',
                        color: t.tone,
                        borderLeft: '2px solid white',
                      }}
                    >
                      {t.label}{' '}
                      <span style={{ fontWeight: 400, fontSize: 13 }}>· {METHODS[mk].label}</span>
                    </th>
                  );
                })}
                <th rowSpan={2} className="num" style={{ width: 130, background: '#f0f0f3' }}>
                  รวมประมาณการ
                </th>
              </tr>
              <tr>
                {types.map((t) => (
                  <Fragment key={t.code}>
                    <th
                      className="num"
                      style={{
                        width: 80,
                        background: t.tone + '08',
                        borderLeft: '2px solid white',
                        fontSize: 13,
                      }}
                    >
                      ก.พ.
                    </th>
                    <th
                      className="num"
                      style={{ width: 80, background: t.tone + '08', fontSize: 13 }}
                    >
                      มี.ค.
                    </th>
                    <th
                      className="num"
                      style={{ width: 80, background: t.tone + '08', fontSize: 13 }}
                    >
                      เม.ย.
                    </th>
                    <th
                      className="num"
                      style={{
                        width: 95,
                        background: t.tone + '20',
                        color: t.tone,
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      พ.ค. ประมาณ
                    </th>
                    <th style={{ width: 50, background: t.tone + '08', fontSize: 13 }}>สถานะ</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => {
                let rowTotal = 0;
                return (
                  <tr key={b.id}>
                    <td
                      style={{ position: 'sticky', left: 0, background: 'white', zIndex: 1 }}
                      className="code"
                    >
                      {b.id}
                    </td>
                    <td
                      style={{
                        position: 'sticky',
                        left: 90,
                        background: 'white',
                        zIndex: 1,
                        borderRight: '2px solid var(--border-strong)',
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>{b.name}</div>
                      <div className="meta">{b.region}</div>
                    </td>
                    {types.map((t) => {
                      const c = getCell(b, t);
                      rowTotal += c.estimate;
                      return (
                        <Fragment key={t.code}>
                          <td
                            className="num tnum muted"
                            style={{ borderLeft: '2px solid #f0f0f3' }}
                          >
                            {fmt(c.m1)}
                          </td>
                          <td className="num tnum muted">{fmt(c.m2)}</td>
                          <td className="num tnum muted">{fmt(c.m3)}</td>
                          <td
                            className="num tnum"
                            style={{
                              background: t.tone + '10',
                              color: t.tone,
                              fontWeight: 700,
                            }}
                          >
                            {fmt(c.estimate)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {c.billArrived ? (
                              <span
                                title={`บิลจริง ${fmt(c.actual ?? 0)} บาท`}
                                style={{ color: 'var(--positive)', fontSize: 14, fontWeight: 600 }}
                              >
                                ✓
                              </span>
                            ) : (
                              <span
                                title="รอบิลจริง"
                                style={{ color: 'var(--warning)', fontSize: 14 }}
                              >
                                ○
                              </span>
                            )}
                          </td>
                        </Fragment>
                      );
                    })}
                    <td className="num tnum" style={{ background: '#fafafa', fontWeight: 700 }}>
                      {fmt(rowTotal)}
                    </td>
                  </tr>
                );
              })}
              <tr className="total">
                <td
                  colSpan={2}
                  style={{
                    position: 'sticky',
                    left: 0,
                    background: '#f4f4f7',
                    zIndex: 1,
                    borderRight: '2px solid var(--border-strong)',
                  }}
                >
                  รวมทุกสาขา
                </td>
                {types.map((t) => {
                  const totalM1 = branches.reduce((s, b) => s + getCell(b, t).m1, 0);
                  const totalM2 = branches.reduce((s, b) => s + getCell(b, t).m2, 0);
                  const totalM3 = branches.reduce((s, b) => s + getCell(b, t).m3, 0);
                  const totalEst = branches.reduce((s, b) => s + getCell(b, t).estimate, 0);
                  return (
                    <Fragment key={t.code}>
                      <td className="num tnum" style={{ borderLeft: '2px solid var(--border)' }}>
                        {fmt(totalM1)}
                      </td>
                      <td className="num tnum">{fmt(totalM2)}</td>
                      <td className="num tnum">{fmt(totalM3)}</td>
                      <td className="num tnum" style={{ background: t.tone, color: 'white' }}>
                        {fmt(totalEst)}
                      </td>
                      <td />
                    </Fragment>
                  );
                })}
                <td className="num tnum" style={{ background: 'var(--brand)', color: 'white' }}>
                  {fmt(
                    branches.reduce(
                      (s, b) => s + EXP_TYPES.reduce((sx, t) => sx + getCell(b, t).estimate, 0),
                      0,
                    ),
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="tbl-footer">
          <span>
            <span className="badge positive" style={{ marginRight: 6 }}>
              ✓
            </span>{' '}
            บิลจริงมาแล้ว · กลับประมาณการเรียบร้อย
            <span style={{ marginLeft: 12 }}>
              <span className="badge warning">○</span> รอบิลจริง · ตั้งประมาณการไว้
            </span>
          </span>
          <span>
            แสดง {branches.length} จาก {ACC_BRANCHES.length} สาขา
          </span>
        </div>
      </div>
    </>
  );
}

interface OutstandingProps {
  list: readonly OutstandingItem[];
  onBookActual: (o: OutstandingItem) => void;
}

function _OutstandingList({ list, onBookActual }: OutstandingProps) {
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          รายการที่ตั้งประมาณการไว้แล้ว · รอบิลจริง
        </span>
        <select className="select">
          <option>ประเภท: ทุกประเภท</option>
          {EXP_TYPES.map((t) => (
            <option key={t.code}>{t.label}</option>
          ))}
        </select>
        <select className="select">
          <option>เรียงตาม: ค้างนาน → ใหม่</option>
          <option>มูลค่า มาก → น้อย</option>
          <option>สาขา</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button
            className="btn sm"
            onClick={() => {
              /* TODO(phase-3): wire */
            }}
          >
            ส่ง Email แจ้งสาขา
          </button>
        </div>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 560 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 24 }}>
                <input type="checkbox" />
              </th>
              <th>เลขที่ Accrual</th>
              <th>สาขา</th>
              <th>ประเภทค่าใช้จ่าย</th>
              <th>วิธีประมาณการ</th>
              <th className="num">เฉลี่ย 3 เดือน</th>
              <th className="num">เดือนล่าสุด</th>
              <th className="num">ประมาณการ พ.ค.</th>
              <th>JV ตอนตั้ง</th>
              <th>อายุค้าง</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {list.slice(0, 50).map((o) => {
              const avg = Math.round((o.m1 + o.m2 + o.m3) / 3);
              return (
                <tr key={o.id}>
                  <td>
                    <input type="checkbox" />
                  </td>
                  <td className="code">
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{o.id}</a>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{o.branch.name}</div>
                    <div className="meta">
                      {o.branch.id} · {o.branch.region}
                    </div>
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                      <span
                        style={{ width: 9, height: 9, borderRadius: 2, background: o.exp.tone }}
                      />
                      {o.exp.label}
                    </span>
                  </td>
                  <td>
                    <span className="badge neutral" style={{ fontSize: 13 }}>
                      {METHODS[o.exp.method].icon} {METHODS[o.exp.method].label}
                    </span>
                  </td>
                  <td className="num tnum muted">{fmt(avg)}</td>
                  <td className="num tnum muted">{fmt(o.m3)}</td>
                  <td className="num tnum">
                    <b style={{ color: o.exp.tone }}>{fmt(o.estimate)}</b>
                  </td>
                  <td className="code muted">
                    JV-2026-04-AC{String(parseInt(o.branch.id.slice(3))).padStart(3, '0')}
                  </td>
                  <td>
                    {o.daysOld <= 30 ? (
                      <span className="badge info" style={{ fontSize: 13 }}>
                        {o.daysOld} วัน
                      </span>
                    ) : o.daysOld <= 60 ? (
                      <span className="badge warning" style={{ fontSize: 13 }}>
                        {o.daysOld} วัน
                      </span>
                    ) : (
                      <span className="badge negative" style={{ fontSize: 13 }}>
                        {o.daysOld} วัน
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn sm primary"
                      style={{ height: 28, fontSize: 14 }}
                      onClick={() => onBookActual(o)}
                    >
                      บันทึกบิลจริง →
                    </button>
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td colSpan={7}>รวมรอบิลจริง {list.length} รายการ</td>
              <td className="num tnum">{fmt(list.reduce((s, o) => s + o.estimate, 0))}</td>
              <td colSpan={3} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface BookingProps {
  onBook: (o: OutstandingItem) => void;
  outstandingList: readonly OutstandingItem[];
}

function BookingWorkflow({ onBook, outstandingList }: BookingProps) {
  const [searchBranch, setSearchBranch] = useState<string>('');
  const [searchExp, setSearchExp] = useState<string>('all');

  const matches = outstandingList
    .filter((o) => {
      if (
        searchBranch &&
        !o.branch.name.includes(searchBranch) &&
        !o.branch.id.includes(searchBranch)
      )
        return false;
      if (searchExp !== 'all' && o.exp.code !== searchExp) return false;
      return true;
    })
    .slice(0, 8);

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div>
            <h3>ค้นหาประมาณการที่ตั้งไว้ — ก่อนบันทึกบิลจริง</h3>
            <div className="sub">
              เมื่อบิลค่าไฟ ค่าน้ำ ค่า Internet มาถึง ระบุสาขา + ประเภท เพื่อให้ระบบจับคู่ accrual
              ที่ค้างอยู่
            </div>
          </div>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                สาขา
              </label>
              <input
                className="input"
                placeholder="พิมพ์รหัสสาขา หรือชื่อสาขา (เช่น สีลม, BR-001)"
                value={searchBranch}
                onChange={(e) => setSearchBranch(e.target.value)}
                style={{ width: '100%', height: 40, fontSize: 15 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ประเภทค่าใช้จ่าย
              </label>
              <select
                className="select"
                value={searchExp}
                onChange={(e) => setSearchExp(e.target.value)}
                style={{ width: '100%', height: 40, fontSize: 14 }}
              >
                <option value="all">ทุกประเภท</option>
                {EXP_TYPES.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่ในบิล
              </label>
              <input
                className="input"
                type="date"
                defaultValue="2026-05-23"
                style={{ width: '100%', height: 40, fontSize: 14 }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>
            {matches.length > 0
              ? `พบประมาณการที่ค้างอยู่ ${matches.length} รายการ`
              : 'ยังไม่พบประมาณการที่ตรงกัน'}
          </h3>
          <span className="meta">เลือกเพื่อบันทึกบิลจริงและกลับประมาณการ</span>
        </div>
        <div className="card-body flush">
          {matches.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              <Icon name="search" size={32} />
              <div style={{ marginTop: 10, fontSize: 15 }}>
                กรุณาระบุสาขาเพื่อค้นหาประมาณการที่ตั้งไว้
              </div>
              <div className="meta" style={{ marginTop: 4 }}>
                หรือคลิก "บันทึกค่าใช้จ่ายใหม่" หากไม่ได้ตั้งประมาณการไว้
              </div>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>เลขที่ Accrual</th>
                  <th>สาขา</th>
                  <th>ประเภท</th>
                  <th className="num">ประมาณการ</th>
                  <th>วิธีคำนวณ</th>
                  <th>อายุ</th>
                  <th style={{ width: 140 }}>การกระทำ</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((o) => (
                  <tr key={o.id}>
                    <td className="code">
                      <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{o.id}</a>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{o.branch.name}</div>
                      <div className="meta">{o.branch.id}</div>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <span
                          style={{ width: 9, height: 9, borderRadius: 2, background: o.exp.tone }}
                        />
                        {o.exp.label}
                      </span>
                    </td>
                    <td className="num tnum">
                      <b style={{ color: o.exp.tone }}>{fmt(o.estimate)}</b>
                    </td>
                    <td className="muted">{METHODS[o.exp.method].label}</td>
                    <td>
                      <span className="badge info" style={{ fontSize: 13 }}>
                        {o.daysOld} วัน
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn sm primary"
                        style={{ height: 28, fontSize: 14 }}
                        onClick={() => onBook(o)}
                      >
                        เลือก & บันทึก →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div
        style={{
          marginTop: 'var(--section-gap)',
          padding: '14px 18px',
          background: '#fef3e8',
          borderRadius: 8,
          border: '1px solid #f0dab0',
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'var(--warning)',
            color: 'white',
            display: 'grid',
            placeItems: 'center',
            fontSize: 16,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          !
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.6 }}>
          <b>เหตุใดต้องค้นหาประมาณการก่อนบันทึก:</b> หากบันทึกบิลจริงโดยไม่กลับประมาณการก่อน
          ค่าใช้จ่ายในงบกำไรขาดทุนจะถูกบันทึก<b>ซ้ำซ้อน</b>
          (1 ครั้งจากประมาณการ + 1 ครั้งจากบิลจริง) ระบบจึงบังคับให้ตรวจสอบทุกครั้ง ·
          หากไม่ได้ตั้งประมาณการไว้ สามารถบันทึกเข้าค่าใช้จ่ายตามปกติ
          แต่ต้องระบุเหตุผลเพื่อตรวจสอบย้อนหลัง
        </div>
      </div>
    </>
  );
}

interface MethodSettingsProps {
  methods: Record<string, MethodKey>;
  setMethods: (m: Record<string, MethodKey>) => void;
}

function MethodSettings({ methods, setMethods }: MethodSettingsProps) {
  return (
    <div className="grid-2-eq">
      <div className="card">
        <div className="card-head">
          <h3>วิธีประมาณการ — ตั้งตามประเภทค่าใช้จ่าย</h3>
          <span className="meta">ใช้กับการรันประมาณการเดือนถัดไป</span>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>ประเภท</th>
                <th>บัญชี</th>
                <th>ลักษณะ</th>
                <th>วิธีประมาณการ</th>
                <th>ความแปรปรวน</th>
              </tr>
            </thead>
            <tbody>
              {EXP_TYPES.map((t) => {
                const mk = methods[t.code] ?? t.method;
                return (
                  <tr key={t.code}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{ width: 10, height: 10, borderRadius: 2, background: t.tone }}
                        />
                        <b>{t.label}</b>
                      </span>
                    </td>
                    <td className="code muted">{t.acct}</td>
                    <td className="muted">{t.description}</td>
                    <td>
                      <select
                        className="select"
                        value={mk}
                        onChange={(e) =>
                          setMethods({ ...methods, [t.code]: e.target.value as MethodKey })
                        }
                        style={{ height: 32, fontSize: 14 }}
                      >
                        {(
                          Object.entries(METHODS) as readonly [
                            MethodKey,
                            { label: string; icon: string },
                          ][]
                        ).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v.icon} {v.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                          style={{
                            width: 60,
                            height: 6,
                            borderRadius: 3,
                            background: '#f0f0f3',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${t.variance * 200}%`,
                              height: '100%',
                              background: t.tone,
                            }}
                          />
                        </div>
                        <span className="meta">±{(t.variance * 100).toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div
          style={{
            padding: '12px 14px',
            background: '#fafafa',
            fontSize: 14,
            color: 'var(--text-muted)',
            lineHeight: 1.55,
          }}
        >
          <b>คำแนะนำ:</b> ค่าใช้จ่ายที่มีลักษณะคงที่ (Internet, รปภ., ทำความสะอาด) ใช้ "เดือนล่าสุด"
          จะแม่นยำกว่า · ค่าใช้จ่ายที่ผันแปร (ค่าไฟฟ้า) ควรใช้ "ค่าเฉลี่ย 3 เดือน"
          เพื่อลดผลกระทบจากเดือนพิเศษ · เลือก "เดือนเดียวกันปีก่อน" สำหรับค่าใช้จ่ายตามฤดูกาล (เช่น
          ค่าไฟฤดูร้อน)
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
        <div className="card">
          <div className="card-head">
            <h3>กำหนดการรันประมาณการ (Schedule)</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label
                  style={{
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  วันที่รันประมาณการประจำเดือน
                </label>
                <select
                  className="select"
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                  defaultValue="25"
                >
                  <option value="20">วันที่ 20 ของเดือน</option>
                  <option value="25">วันที่ 25 ของเดือน</option>
                  <option value="last-5">5 วันก่อนสิ้นเดือน</option>
                  <option value="last-2">2 วันก่อนสิ้นเดือน</option>
                  <option value="manual">รันด้วยมือเท่านั้น</option>
                </select>
              </div>
              <div>
                <label
                  style={{
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  วันที่ลงบัญชีของ JV
                </label>
                <select
                  className="select"
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                  defaultValue="last"
                >
                  <option value="last">วันสุดท้ายของเดือน</option>
                  <option value="run">วันที่รันประมาณการ</option>
                </select>
              </div>
              <label
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                <input type="checkbox" defaultChecked />
                <span>เตือนทาง Email หากมี accrual ค้างเกิน 60 วัน</span>
              </label>
              <label
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                <input type="checkbox" defaultChecked />
                <span>บังคับให้ค้นหา accrual ก่อนบันทึกค่าใช้จ่ายจริง</span>
              </label>
              <label
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                <input type="checkbox" />
                <span>เตือนเมื่อยอดบิลจริงเบี่ยงเบนจากประมาณการเกิน 20%</span>
              </label>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>ประวัติการรันประมาณการ</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>งวด</th>
                  <th>วันรัน</th>
                  <th className="num">รายการ</th>
                  <th className="num">มูลค่า</th>
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>พ.ค. 2026</td>
                  <td>25 เม.ย. 2026</td>
                  <td className="num">{ACC_BRANCHES.length * EXP_TYPES.length}</td>
                  <td className="num tnum">{fmt(2_960_000)}</td>
                  <td>
                    <span className="badge positive">
                      <span className="bdot" />
                      Posted
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>เม.ย. 2026</td>
                  <td>25 มี.ค. 2026</td>
                  <td className="num">{ACC_BRANCHES.length * EXP_TYPES.length}</td>
                  <td className="num tnum">{fmt(2_840_000)}</td>
                  <td>
                    <span className="badge positive">
                      <span className="bdot" />
                      Posted
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>มี.ค. 2026</td>
                  <td>25 ก.พ. 2026</td>
                  <td className="num">{ACC_BRANCHES.length * EXP_TYPES.length}</td>
                  <td className="num tnum">{fmt(2_780_000)}</td>
                  <td>
                    <span className="badge positive">
                      <span className="bdot" />
                      Posted
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>ก.พ. 2026</td>
                  <td>25 ม.ค. 2026</td>
                  <td className="num">{ACC_BRANCHES.length * EXP_TYPES.length}</td>
                  <td className="num tnum">{fmt(2_720_000)}</td>
                  <td>
                    <span className="badge positive">
                      <span className="bdot" />
                      Posted
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

interface BookActualModalProps {
  item: OutstandingItem;
  onClose: () => void;
}

function BookActualModal({ item, onClose }: BookActualModalProps) {
  const [actual, setActual] = useState<number>(item.estimate);
  const [vatIncluded, setVatIncluded] = useState<boolean>(true);
  const [invoice, setInvoice] = useState<string>('');

  const diff = actual - item.estimate;
  const diffPct = (diff / item.estimate) * 100;
  const exVat = vatIncluded ? actual / 1.07 : actual;
  const vatAmount = vatIncluded ? actual - exVat : actual * 0.07;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 980,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              บันทึกค่าใช้จ่ายจริง · กลับประมาณการ
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              <b style={{ color: '#3f3f46' }}>{item.exp.label}</b> ·{' '}
              <b style={{ color: '#3f3f46' }}>{item.branch.name}</b> ({item.branch.id}) · งวด พ.ค.
              2026
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: '18px 22px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflow: 'auto',
            flex: 1,
          }}
        >
          <div
            style={{
              padding: '14px 16px',
              background: '#fef3e8',
              border: '1px solid #f0dab0',
              borderLeft: '4px solid var(--warning)',
              borderRadius: 8,
              display: 'flex',
              gap: 14,
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'var(--warning)',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name="refresh" size={20} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                ระบบตรวจพบประมาณการที่ค้างอยู่ — ต้องกลับประมาณการก่อนบันทึกบิลจริง
              </div>
              <div className="meta" style={{ marginTop: 2 }}>
                Accrual: <b style={{ color: '#3f3f46' }}>{item.id}</b> · ตั้งเมื่อ{' '}
                <b style={{ color: '#3f3f46' }}>30 เม.ย. 2026</b> · ยอดประมาณการ{' '}
                <b style={{ color: 'var(--warning)' }}>{fmt(item.estimate)}</b> บาท
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                เลขที่ใบกำกับภาษี / ใบเสร็จ
              </label>
              <input
                className="input code"
                placeholder="เช่น 65/04/12345"
                value={invoice}
                onChange={(e) => setInvoice(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่ในบิล
              </label>
              <input
                className="input"
                type="date"
                defaultValue="2026-05-20"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                กำหนดชำระ
              </label>
              <input
                className="input"
                type="date"
                defaultValue="2026-06-10"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>

          <div
            style={{
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          >
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              ยอดประมาณการ vs ยอดจริง
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr auto 1fr',
                gap: 14,
                alignItems: 'center',
              }}
            >
              <div>
                <div className="meta">ยอดประมาณการที่ตั้งไว้</div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 600,
                    marginTop: 4,
                    color: 'var(--warning)',
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(item.estimate)}
                </div>
                <div className="meta">{METHODS[item.exp.method].label}</div>
              </div>
              <div style={{ fontSize: 22, color: 'var(--text-faint)' }}>→</div>
              <div>
                <div className="meta">ยอดบิลจริง (รวม VAT)</div>
                <input
                  className="input tnum"
                  type="number"
                  value={Math.round(actual)}
                  onChange={(e) => setActual(parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    height: 42,
                    fontSize: 18,
                    textAlign: 'right',
                    fontWeight: 700,
                  }}
                />
                <label
                  style={{
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                    fontSize: 13,
                    marginTop: 6,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={vatIncluded}
                    onChange={(e) => setVatIncluded(e.target.checked)}
                  />
                  <span>ยอดข้างต้นรวม VAT 7% แล้ว</span>
                </label>
              </div>
              <div style={{ fontSize: 22, color: 'var(--text-faint)' }}>=</div>
              <div>
                <div className="meta">ผลต่าง</div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    marginTop: 4,
                    color:
                      Math.abs(diffPct) > 20
                        ? 'var(--negative)'
                        : diff > 0
                          ? 'var(--negative)'
                          : 'var(--positive)',
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {diff > 0 ? '+' : ''}
                  {fmt(Math.round(diff))}
                </div>
                <div
                  className="meta"
                  style={{ color: Math.abs(diffPct) > 20 ? 'var(--negative)' : 'inherit' }}
                >
                  {diff > 0 ? '+' : ''}
                  {diffPct.toFixed(1)}% {Math.abs(diffPct) > 20 && '· เบี่ยงเบนเกิน 20%'}
                </div>
              </div>
            </div>
            {Math.abs(diffPct) > 20 && (
              <div
                style={{
                  marginTop: 12,
                  padding: '8px 12px',
                  background: '#fce9e7',
                  border: '1px solid #f4c1bd',
                  borderRadius: 6,
                  fontSize: 13.5,
                  color: 'var(--negative)',
                }}
              >
                ⚠ ยอดเบี่ยงเบนจากประมาณการเกิน 20% — กรุณาตรวจสอบกับสาขาว่ามีการใช้ผิดปกติหรือไม่
                และอาจต้องปรับวิธีประมาณการเดือนถัดไป
              </div>
            )}
          </div>

          <div>
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              ระบบจะสร้างใบสำคัญ 2 ขั้นตอนอัตโนมัติ
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div
                style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    background: '#fef3e8',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      background: '#b42318',
                      color: 'white',
                      display: 'grid',
                      placeItems: 'center',
                      fontWeight: 700,
                      fontSize: 14,
                      fontFamily: 'var(--font-display)',
                      flexShrink: 0,
                    }}
                  >
                    1
                  </div>
                  <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                    กลับประมาณการ — ลบประมาณการเดิม {fmt(item.estimate)} บาท ออก
                  </div>
                  <span className="code muted" style={{ fontSize: 13 }}>
                    JV-2026-05-RA
                    {String(parseInt(item.branch.id.slice(3))).padStart(3, '0')}.1
                  </span>
                </div>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>บัญชี</th>
                      <th>ชื่อบัญชี</th>
                      <th className="num" style={{ width: 160 }}>
                        เดบิต
                      </th>
                      <th className="num" style={{ width: 160 }}>
                        เครดิต
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="code">2150</td>
                      <td>
                        ค่าใช้จ่ายค้างจ่าย — {item.exp.label} · {item.branch.id}
                      </td>
                      <td className="num tnum">{fmt(item.estimate)}</td>
                      <td className="num">—</td>
                    </tr>
                    <tr>
                      <td className="code">{item.exp.acct}</td>
                      <td>{item.exp.label} — กลับประมาณการ พ.ค. 2026</td>
                      <td className="num">—</td>
                      <td className="num tnum">{fmt(item.estimate)}</td>
                    </tr>
                    <tr className="total">
                      <td colSpan={2}>รวม</td>
                      <td className="num tnum">{fmt(item.estimate)}</td>
                      <td className="num tnum">{fmt(item.estimate)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    background: '#e7f4ec',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      background: '#117a3d',
                      color: 'white',
                      display: 'grid',
                      placeItems: 'center',
                      fontWeight: 700,
                      fontSize: 14,
                      fontFamily: 'var(--font-display)',
                      flexShrink: 0,
                    }}
                  >
                    2
                  </div>
                  <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                    บันทึกค่าใช้จ่ายจริง — {fmt(Math.round(exVat))} + VAT{' '}
                    {fmt(Math.round(vatAmount))} บาท
                  </div>
                  <span className="code muted" style={{ fontSize: 13 }}>
                    JV-2026-05-RA
                    {String(parseInt(item.branch.id.slice(3))).padStart(3, '0')}.2
                  </span>
                </div>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>บัญชี</th>
                      <th>ชื่อบัญชี</th>
                      <th className="num" style={{ width: 160 }}>
                        เดบิต
                      </th>
                      <th className="num" style={{ width: 160 }}>
                        เครดิต
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="code">{item.exp.acct}</td>
                      <td>
                        {item.exp.label} — บิลจริง พ.ค. 2026 · {item.branch.id}
                      </td>
                      <td className="num tnum">{fmt(Math.round(exVat))}</td>
                      <td className="num">—</td>
                    </tr>
                    {vatIncluded && (
                      <tr>
                        <td className="code">1131</td>
                        <td>ภาษีซื้อ (Input VAT 7%)</td>
                        <td className="num tnum">{fmt(Math.round(vatAmount))}</td>
                        <td className="num">—</td>
                      </tr>
                    )}
                    <tr>
                      <td className="code">2110</td>
                      <td>เจ้าหนี้การค้า / ค่าใช้จ่ายค้างชำระ</td>
                      <td className="num">—</td>
                      <td className="num tnum">{fmt(Math.round(actual))}</td>
                    </tr>
                    <tr className="total">
                      <td colSpan={2}>รวม</td>
                      <td className="num tnum">{fmt(Math.round(actual))}</td>
                      <td className="num tnum">{fmt(Math.round(actual))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div
            style={{
              padding: '14px 16px',
              background: 'var(--brand-tint)',
              border: '1px solid var(--brand-tint-2)',
              borderLeft: '4px solid var(--brand)',
              borderRadius: 8,
            }}
          >
            <div className="section-title" style={{ margin: '0 0 10px', color: 'var(--brand)' }}>
              ผลกระทบต่อ P&L หลังการบันทึก
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
              <div>
                <div className="meta">{item.exp.label} (สุทธิหลังกลับประมาณการ)</div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    marginTop: 4,
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(Math.round(exVat))}
                </div>
                <div className="meta">
                  = {fmt(item.estimate)} (ประมาณ) − {fmt(item.estimate)} (กลับ) +{' '}
                  {fmt(Math.round(exVat))} (จริง)
                </div>
              </div>
              <div>
                <div className="meta">ค่าใช้จ่ายค้างจ่าย (2150)</div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    marginTop: 4,
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(0)}
                </div>
                <div className="meta">หักออกครบ ปิดบัญชีรายการนี้</div>
              </div>
              <div>
                <div className="meta">เจ้าหนี้ (2110) เพิ่มขึ้น</div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    marginTop: 4,
                    color: 'var(--negative)',
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(Math.round(actual))}
                </div>
                <div className="meta">รอจ่ายให้ผู้ขาย</div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              บันทึกฉบับร่าง
            </button>
            <button
              className="btn primary"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              <Icon name="check" size={14} /> ยืนยัน & สร้าง JV ทั้ง 2 ใบ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface RunModalProps {
  totals: Totals;
  branches: readonly Branch[];
  methods: Record<string, MethodKey>;
  onClose: () => void;
  isRunning: boolean;
  onConfirm: () => void;
}

function RunModal({ totals, branches, methods, onClose, isRunning, onConfirm }: RunModalProps) {
  const byType = EXP_TYPES.map((t) => ({
    ...t,
    method: methods[t.code] ?? t.method,
    count: branches.length,
    amount: branches.reduce((s, b) => s + getCell(b, t).estimate, 0),
  }));
  const grandTotal = byType.reduce((s, t) => s + t.amount, 0);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 880,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              รันประมาณการค่าใช้จ่ายประจำ — งวด พ.ค. 2026
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              ระบบจะสร้าง <b style={{ color: '#3f3f46' }}>{branches.length * EXP_TYPES.length}</b>{' '}
              รายการ ใน 1 batch (JV-2026-05-ACC15) · ยอดประมาณการรวม{' '}
              <b style={{ color: 'var(--brand)' }}>{fmt(grandTotal)}</b> บาท
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: '18px 22px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflow: 'auto',
            flex: 1,
          }}
        >
          <div>
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              สรุปตามประเภทค่าใช้จ่าย
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 24 }}>
                      <input type="checkbox" defaultChecked />
                    </th>
                    <th>ประเภท</th>
                    <th>วิธีประมาณการ</th>
                    <th>บัญชีค่าใช้จ่าย</th>
                    <th>บัญชีค้างจ่าย</th>
                    <th className="num">จำนวนสาขา</th>
                    <th className="num">ยอดประมาณการรวม</th>
                  </tr>
                </thead>
                <tbody>
                  {byType.map((t) => (
                    <tr key={t.code}>
                      <td>
                        <input type="checkbox" defaultChecked />
                      </td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span
                            style={{ width: 10, height: 10, borderRadius: 2, background: t.tone }}
                          />
                          <b>{t.label}</b>
                        </span>
                      </td>
                      <td>
                        <span className="badge neutral" style={{ fontSize: 13 }}>
                          {METHODS[t.method].icon} {METHODS[t.method].label}
                        </span>
                      </td>
                      <td className="code muted">{t.acct}</td>
                      <td className="code muted">2150</td>
                      <td className="num">{t.count}</td>
                      <td className="num tnum">
                        <b>{fmt(t.amount)}</b>
                      </td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td colSpan={5}>รวมทุกประเภท · ทุกสาขา</td>
                    <td className="num">{branches.length * EXP_TYPES.length}</td>
                    <td className="num tnum" style={{ color: 'var(--brand)' }}>
                      {fmt(grandTotal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              ตัวอย่างการลงบัญชี (ทุกรายการในรูปแบบเดียวกัน)
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>บัญชี</th>
                    <th>ชื่อบัญชี</th>
                    <th className="num">เดบิต</th>
                    <th className="num">เครดิต</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="code">5131</td>
                    <td>ค่าไฟฟ้า — สาขา BR-001 (สีลม) · ประมาณการ พ.ค. 2026</td>
                    <td className="num tnum">{fmt(12_280)}</td>
                    <td className="num">—</td>
                  </tr>
                  <tr>
                    <td className="code">2150</td>
                    <td>ค่าใช้จ่ายค้างจ่าย — BR-001</td>
                    <td className="num">—</td>
                    <td className="num tnum">{fmt(12_280)}</td>
                  </tr>
                  <tr>
                    <td
                      colSpan={4}
                      className="muted faint"
                      style={{ textAlign: 'center', padding: '6px' }}
                    >
                      ... และอีก {branches.length * EXP_TYPES.length - 1} รายการในรูปแบบเดียวกัน
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          >
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              ตั้งค่าการลงบัญชี
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label
                  style={{
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  วันที่ลงบัญชี
                </label>
                <input
                  className="input"
                  type="date"
                  defaultValue="2026-05-31"
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  ผู้รับผิดชอบ
                </label>
                <select className="select" style={{ width: '100%', height: 38, fontSize: 14 }}>
                  <option>สมชาย กิตติ — ผจก. บัญชี</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                <input type="checkbox" defaultChecked />
                <span>ข้ามรายการที่ได้รับบิลจริงแล้ว ({totals.billedCount} รายการ)</span>
              </label>
              <label
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                <input type="checkbox" defaultChecked />
                <span>สร้าง Reminder อัตโนมัติ — แจ้งเตือนหากบิลจริงไม่มาภายใน 60 วัน</span>
              </label>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              Preview ทุกรายการ
            </button>
            <button className="btn primary" disabled={isRunning} onClick={onConfirm}>
              <Icon name="check" size={14} />{' '}
              {isRunning ? 'กำลังประมวลผล...' : 'ประมวลผล & ลง JV ทุกรายการ'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
